/**
 * DevDocsArticle — Single developer doc article page
 * Left sidebar nav + main content + right TOC
 */

import { h } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { route } from 'preact-router';

import { T } from '../styles/tokens';
import { Button } from '../components/ui';
const API_BASE = '/api/v1/cms';

interface Article {
  id: string; slug: string; category_id: string; author: string;
  view_count: number; helpful_yes: number; helpful_no: number;
  api_endpoint: string; api_method: string; code_language: string;
  title: string; content: string; excerpt: string;
  published_at: string; created_at: string;
}
interface Category { slug: string; name: string }
interface Related { slug: string; title: string; excerpt: string }
interface NavCategory { id: string; slug: string; icon: string; name: string; article_count: number }

const METHOD_COLORS: Record<string, string> = {
  GET: T.success, POST: T.primary, PUT: T.warning, DELETE: T.danger, PATCH: T.purple,
};

const ICONS: Record<string, string> = {
  rocket: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09zM12 15l-3-3 10-10 3 3z',
  book: 'M4 19.5A2.5 2.5 0 016.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z',
  code: 'M16 18l6-6-6-6M8 6l-6 6 6 6',
  terminal: 'M4 17l6-6-6-6M12 19h8',
  package: 'M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12',
};

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif", display: 'flex' } as any,
  sidebar: { width: 260, borderRight: `1px solid ${T.border}`, padding: '24px 0', flexShrink: 0, overflowY: 'auto' as const, position: 'sticky' as const, top: 0, height: '100vh' } as any,
  sidebarTitle: { fontSize: 18, fontWeight: 700, padding: '0 20px', marginBottom: 20, cursor: 'pointer', letterSpacing: '-0.02em' },
  navItem: (active: boolean) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', fontSize: 14,
    color: active ? T.primary : T.textSecondary, cursor: 'pointer', transition: 'all 0.15s',
    background: active ? T.primaryBg : 'transparent',
    borderLeft: active ? '2px solid #5B8DEF' : '2px solid transparent',
  }),
  main: { flex: 1, padding: '40px 48px', maxWidth: 800, minWidth: 0 } as any,
  toc: { width: 200, flexShrink: 0, padding: '40px 16px', position: 'sticky' as const, top: 0, height: '100vh', overflowY: 'auto' as const } as any,
  tocTitle: { fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: 12 },
  tocItem: { display: 'block', fontSize: 13, color: T.textMuted, padding: '3px 0', cursor: 'pointer', textDecoration: 'none', transition: 'color 0.15s' },
  breadcrumbs: { fontSize: 13, color: T.textMuted, marginBottom: 20, display: 'flex', gap: 8, alignItems: 'center' } as any,
  breadcrumbLink: { color: T.primary, cursor: 'pointer', textDecoration: 'none' },
  title: { fontSize: 28, fontWeight: 700, marginBottom: 12, lineHeight: 1.3, letterSpacing: '-0.02em' },
  badge: (color: string) => ({ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 4, background: `${color}20`, color, fontFamily: 'monospace' }),
  content: { fontSize: 15, lineHeight: 1.8, color: '#C0C8D8' } as any,
  feedback: { display: 'flex', gap: 12, alignItems: 'center', marginTop: 40, padding: '20px 0', borderTop: `1px solid ${T.border}` } as any,
  feedbackBtn: (active: boolean) => ({
    padding: '6px 14px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
    background: active ? T.primaryBg : T.bgCardHover,
    border: '1px solid ' + (active ? T.primaryBg : T.border),
    color: active ? T.primary : T.textSecondary, transition: 'all 0.15s',
  }),
  related: { marginTop: 24, padding: '20px 0', borderTop: `1px solid ${T.border}` } as any,
  relatedTitle: { fontSize: 14, fontWeight: 600, color: T.textSecondary, marginBottom: 12 },
  relatedItem: { fontSize: 14, color: T.primary, cursor: 'pointer', padding: '4px 0', display: 'block', textDecoration: 'none' },
  skeleton: { background: T.bgCardHover, borderRadius: 8, height: 300, marginBottom: 16 } as any,
};

/** Replace {{CODE_BLOCK_N}} placeholders with actual code blocks */
function injectCodeBlocks(md: string, codeBlocksJson: string | null | undefined): string {
  if (!codeBlocksJson) return md;
  try {
    const blocks: { id: number; language: string; label: string; code: string }[] =
      typeof codeBlocksJson === 'string' ? JSON.parse(codeBlocksJson) : codeBlocksJson;
    if (!Array.isArray(blocks) || blocks.length === 0) return md;
    return md.replace(/\{\{CODE_BLOCK_(\d+)\}\}/g, (_match, idStr) => {
      const id = parseInt(idStr, 10);
      const block = blocks.find(b => b.id === id);
      if (!block) return _match;
      return `\n\`\`\`${block.language || ''}\n${block.code}\n\`\`\`\n`;
    });
  } catch { return md; }
}

/** Simple markdown renderer */
function renderMarkdown(md: string): string {
  if (!md) return '';
  let html = md
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
      const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const rawForCopy = code.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      return `<div style="position:relative;margin:16px 0"><pre style="background:var(--t-white-alpha-5);padding:16px;padding-right:56px;border-radius:8px;overflow-x:auto;font-size:13px;line-height:1.5;margin:0"><code class="lang-${lang}">${escaped}</code></pre><Button variant="ghost" onclick="(function(b){var t=b.closest('div').querySelector('code').innerText;navigator.clipboard.writeText(t).then(function(){b.textContent='Copiado!';setTimeout(function(){b.textContent='Copiar'},1500)}).catch(function(){var a=document.createElement('textarea');a.value=t;document.body.appendChild(a);a.select();document.execCommand('copy');document.body.removeChild(a);b.textContent='Copiado!';setTimeout(function(){b.textContent='Copiar'},1500)})})(this)" style="position:absolute;top:8px;right:8px;background:rgba(255,255,255,0.1);border:1px solid var(--t-white-alpha-15);color:rgba(255,255,255,0.7);padding:4px 10px;border-radius:6px;font-size:12px;cursor:pointer;transition:all 0.2s" onmouseover="this.style.background=T.borderHover;this.style.color='#fff'" onmouseout="this.style.background=T.bgActive;this.style.color=T.text">Copiar</Button></div>`;
    })
    .replace(/`([^`]+)`/g, `<code style="background:${T.border};padding:2px 6px;border-radius:4px;font-size:13px">$1</code>`)
    .replace(/^### (.+)$/gm, `<h3 id="$1" style="font-size:18px;font-weight:600;margin:24px 0 8px;color:${T.text}">$1</h3>`)
    .replace(/^## (.+)$/gm, `<h2 id="$1" style="font-size:20px;font-weight:600;margin:28px 0 10px;color:${T.text}">$1</h2>`)
    .replace(/^# (.+)$/gm, `<h1 id="$1" style="font-size:24px;font-weight:700;margin:32px 0 12px;color:${T.text}">$1</h1>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#5B8DEF;text-decoration:none" target="_blank" rel="noopener">$1</a>')
    .replace(/^\|(.+)\|$/gm, (line) => {
      const cells = line.split('|').filter(Boolean).map(c => c.trim());
      return '<tr>' + cells.map(c => `<td style="padding:6px 12px;border:1px solid ${T.border}">${c}</td>`).join('') + '</tr>';
    })
    .replace(/^- (.+)$/gm, '<li style="margin:4px 0;padding-left:4px">$1</li>')
    .replace(/\n\n/g, '</p><p style="margin:12px 0">')
    .replace(/\n/g, '<br/>');

  html = html.replace(/((?:<li[^>]*>.*?<\/li>\s*)+)/g, '<ul style="margin:12px 0;padding-left:20px">$1</ul>');
  html = html.replace(/((?:<tr>.*?<\/tr>\s*)+)/g, '<table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px">$1</table>');

  return `<p style="margin:12px 0">${html}</p>`;
}

/** Extract headings from markdown for TOC */
function extractHeadings(md: string): { level: number; text: string }[] {
  const headings: { level: number; text: string }[] = [];
  const regex = /^(#{1,3}) (.+)$/gm;
  let match;
  while ((match = regex.exec(md)) !== null) {
    headings.push({ level: match[1].length, text: match[2] });
  }
  return headings;
}

export function DevDocsArticlePage(_props: { path?: string; locale?: string; categorySlug?: string; articleSlug?: string }) {
  const [article, setArticle] = useState<Article | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [related, setRelated] = useState<Related[]>([]);
  const [categories, setCategories] = useState<NavCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedbackSent, setFeedbackSent] = useState<boolean | null>(null);

  const locale = _props.locale || 'pt-BR';
  const categorySlug = _props.categorySlug || '';
  const articleSlug = _props.articleSlug || '';

  useEffect(() => {
    fetch(`${API_BASE}/categories?contentType=dev-docs&locale=${encodeURIComponent(locale)}`)
      .then(r => r.json())
      .then((res: any) => setCategories(res?.data?.categories || []))
      .catch(() => {});
  }, [locale]);

  useEffect(() => {
    if (!articleSlug) return;
    setLoading(true);
    fetch(`${API_BASE}/entries/${encodeURIComponent(articleSlug)}?contentType=dev-docs&locale=${encodeURIComponent(locale)}`)
      .then(r => r.json())
      .then((res: any) => {
        const entry = res?.data?.entry || null;
        if (entry) { entry.content = entry.body || entry.content || ''; }
        setArticle(entry);
        setCategory(entry ? { id: entry.category_id, slug: entry.category_slug, name: entry.category_name, icon: entry.category_icon } : null);
        setRelated(res?.data?.related || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [articleSlug, locale]);

  const processedContent = useMemo(() => injectCodeBlocks(article?.content || article?.body || '', article?.code_blocks), [article?.content, article?.body, article?.code_blocks]);
  const headings = useMemo(() => extractHeadings(processedContent), [processedContent]);

  const sendFeedback = async (helpful: boolean) => {
    if (!article || feedbackSent !== null) return;
    setFeedbackSent(helpful);
    try {
      await fetch(`${API_BASE}/entries/${article.id}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: helpful ? 'click' : 'view' }),
      });
    } catch { /* ignore */ }
  };

  return (
    <div style={S.page}>
      {/* Left sidebar */}
      <nav style={S.sidebar}>
        <div style={S.sidebarTitle} onClick={() => route(`/${locale}`)}>WhiteLabelClaw Docs</div>
        {categories.map((cat) => (
          <div
            key={cat.id}
            style={S.navItem(cat.slug === categorySlug)}
            onClick={() => route(`/${locale}/${cat.slug}`)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={ICONS[cat.icon] || ICONS.code} />
            </svg>
            {cat.name || cat.slug}
          </div>
        ))}
      </nav>

      {/* Main content */}
      <div style={S.main}>
        {loading && <div style={S.skeleton} />}

        {!loading && !article && (
          <p style={{ color: T.textMuted }}>Artigo nao encontrado.</p>
        )}

        {!loading && article && (
          <div>
            <div style={S.breadcrumbs}>
              <span style={S.breadcrumbLink} onClick={() => route(`/${locale}`)}>Home</span>
              <span>/</span>
              <span style={S.breadcrumbLink} onClick={() => route(`/${locale}/${categorySlug}`)}>{category?.name || categorySlug}</span>
              <span>/</span>
              <span>{article.title}</span>
            </div>

            <h1 style={S.title}>{article.title}</h1>

            {(article.api_method || article.api_endpoint) && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {article.api_method && <span style={S.badge(METHOD_COLORS[article.api_method] || T.textMuted)}>{article.api_method}</span>}
                {article.api_endpoint && <span style={{ ...S.badge(T.borderHover), fontFamily: 'monospace' }}>{article.api_endpoint}</span>}
              </div>
            )}

            <div style={S.content} dangerouslySetInnerHTML={{ __html: renderMarkdown(injectCodeBlocks(article.content || '', article.code_blocks)) }} />

            {/* Feedback */}
            <div style={S.feedback}>
              <span style={{ fontSize: 14, color: T.textSecondary }}>Esta pagina foi util?</span>
              <Button variant={feedbackSent === true ? 'primary' : 'ghost'} size="sm" onClick={() => sendFeedback(true)} style={S.feedbackBtn(feedbackSent === true)}>Sim</Button>
              <Button variant={feedbackSent === false ? 'primary' : 'ghost'} size="sm" onClick={() => sendFeedback(false)} style={S.feedbackBtn(feedbackSent === false)}>Nao</Button>
            </div>

            {/* Related */}
            {related.length > 0 && (
              <div style={S.related}>
                <div style={S.relatedTitle}>Artigos relacionados</div>
                {related.map((r) => (
                  <a key={r.slug} style={S.relatedItem} onClick={() => route(`/${locale}/${categorySlug}/${r.slug}`)}>
                    {r.title}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right TOC */}
      {headings.length > 0 && (
        <div style={S.toc}>
          <div style={S.tocTitle}>Nesta pagina</div>
          {headings.map((h, i) => (
            <a
              key={i}
              style={{ ...S.tocItem, paddingLeft: (h.level - 1) * 12 }}
              onClick={() => {
                const el = document.getElementById(h.text);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {h.text}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
